import React, { useState } from 'react';
import { AppBar, Avatar, Typography, MenuItem, Select, Grid, Button } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles, ThemeProvider } from '@material-ui/styles';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import * as authActions from 'app/auth/store/actions';
import * as Actions from "app/store/actions";

const useStyles = makeStyles(theme => ({
    root: {
        '&.user': {
            '& .username, & .email': {
                transition: theme.transitions.create('opacity', {
                    duration: theme.transitions.duration.shortest,
                    easing: theme.transitions.easing.easeInOut
                })
            }
        }
    },
    avatar: {
        width: 72,
        height: 72,
        position: 'absolute',
        top: 92,
        padding: 8,
        background: theme.palette.background.default,
        boxSizing: 'content-box',
        left: '50%',
        transform: 'translateX(-50%)',
        transition: theme.transitions.create('all', {
            duration: theme.transitions.duration.shortest,
            easing: theme.transitions.easing.easeInOut,
        }),
        '& > img': {
            borderRadius: '50%'
        }
    },
    select: {
        marginTop: 10,
        background: theme.palette.background.default,
        borderRadius: 4,
        padding: 4
    },
    dialogPaper: {
        width: '400px'
    }
}));

function UserNavbarHeader(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const dispatchMsg = useDispatch();

    const user = useSelector(({ auth }) => auth.user);
    const userProfile = user.role[0];
    // El diálogo de perfil vive dentro del layout de la barra lateral, que
    // corre bajo su propio ThemeProvider (navbarTheme, normalmente oscuro).
    // Se lo envuelve con el tema principal para que se vea igual que el
    // resto de los diálogos de selección de perfil de la app.
    const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);
    // Las empresas disponibles ya vienen resueltas desde el login (o desde
    // el último cambio de empresa): admin ve todas, el resto solo las que
    // tienen un perfil asignado.
    const empresas = user.data.empresas || [];

    function message(type = "null", message = "") {
        dispatchMsg(
            Actions.showMessage({
                message: message,
                autoHideDuration: 6000, //ms
                anchorOrigin: {
                    vertical: "top", //top bottom
                    horizontal: "center" //left center right
                },
                variant: type //success error info warning null
            })
        );
    }

    // Empresa seleccionada (la que autorizó el login/el último cambio de empresa)
    const empresaSeleccionada = useSelector(state => state.fuse.empresa?.empresaSeleccionada || user.data.company_id || null);

    const [openPerfil, setOpenPerfil] = useState(false);
    const [perfiles, setPerfiles] = useState([]);
    const [perfilId, setPerfilId] = useState('');
    const [empresaPendiente, setEmpresaPendiente] = useState(null);

    // Cambiar de empresa vuelve a resolver perfil y permisos en el backend
    // (no es solo un cambio de estado local), para no repetir el problema
    // de permisos que no correspondían a la empresa activa. Si el usuario
    // tiene más de un perfil en la empresa elegida, primero hay que
    // preguntarle cuál (la firma y las restricciones dependen del perfil).
    function cambiarEmpresa(companyId, perfil) {
        dispatch(authActions.changeCompany(companyId, perfil)).then((result) => {
            if (result && result.requiresProfile) {
                setEmpresaPendiente(companyId);
                setPerfiles(result.perfiles);
                setPerfilId('');
                setOpenPerfil(true);
            }
        }).catch(() => message("error", "No se pudo cambiar de empresa"));
    }

    const handleEmpresaChange = (event) => {
        const nuevaEmpresa = event.target.value;
        if (nuevaEmpresa === empresaSeleccionada) return;

        cambiarEmpresa(nuevaEmpresa);
    };

    function handleConfirmarPerfil() {
        setOpenPerfil(false);
        cambiarEmpresa(empresaPendiente, perfilId);
    }

    function handleCancelarPerfil() {
        setOpenPerfil(false);
        setPerfilId('');
        setEmpresaPendiente(null);
    }

    return (
        <AppBar
            position="static"
            color="primary"
            elevation={0}
            classes={{ root: classes.root }}
            className="user relative flex flex-col items-center justify-center pt-24 pb-64 mb-32 z-0"
        >
            <Typography className="username text-16 whitespace-no-wrap" color="inherit">{user.data.displayName}</Typography>
            <Typography className="email text-13 mt-8 opacity-50 whitespace-no-wrap" color="inherit">{user.data.email}</Typography>

            {(userProfile === 'admin' || userProfile == 'director' || userProfile == 'rh' || userProfile == 'master') && empresas.length > 1 ?
                < Select
                    value={empresaSeleccionada || ''}
                    onChange={handleEmpresaChange}
                    className={classes.select}
                    variant="outlined"
                    displayEmpty
                >
                    <MenuItem value="">Selecciona una empresa</MenuItem>
                    {empresas && empresas.map((empresa) => (
                        <MenuItem key={empresa.id} value={empresa.id}>{empresa.razon_social}</MenuItem>
                    ))}
                </Select>
                :
                <Avatar
                    className={clsx(classes.avatar, "avatar")}
                    alt="user photo"
                    src={user.data.photoURL && user.data.photoURL !== '' ? user.data.photoURL : "/assets/images/avatars/profile.jpg"}
                />
            }

            <ThemeProvider theme={mainTheme}>
                <Dialog open={openPerfil} onClose={handleCancelarPerfil} classes={{ paper: classes.dialogPaper }} aria-labelledby="form-dialog-title">
                    <DialogTitle id="form-dialog-title">Seleccione el Perfil</DialogTitle>
                    <DialogContent>
                        <Select
                            value={perfilId}
                            onChange={(e) => setPerfilId(e.target.value)}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem value="">Selecciona un perfil</MenuItem>
                            {perfiles.map((item) => (
                                <MenuItem key={item.profile_slug} value={item.profile_slug}>{item.profile_name}</MenuItem>
                            ))}
                        </Select>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelarPerfil} color="primary">
                            Cancelar
                        </Button>
                        <Button onClick={handleConfirmarPerfil} color="primary" disabled={!perfilId}>
                            Continuar
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>

        </AppBar >
    );
}

export default UserNavbarHeader;
